import releaseYearRows from './data/phoneReleaseYears.json'
import { normalizeModelLabel } from './phoneModelMatch'

export const LISTING_RELEASE_YEAR_FLOOR = 2007

export interface PhoneReleaseYearRow {
  manufacturer: string
  model_name: string
  release_year: number
}

function normalizeManufacturer(name: string): string {
  return name.trim().toLowerCase()
}

function catalogKey(manufacturer: string, modelName: string): string {
  return `${normalizeManufacturer(manufacturer)}|${normalizeModelLabel(modelName)}`
}

const RELEASE_YEAR_BY_KEY = new Map<string, number>()
for (const row of releaseYearRows as PhoneReleaseYearRow[]) {
  const key = catalogKey(row.manufacturer, row.model_name)
  const yr = row.release_year
  if (!Number.isFinite(yr)) continue
  const prev = RELEASE_YEAR_BY_KEY.get(key)
  if (prev == null || yr < prev) RELEASE_YEAR_BY_KEY.set(key, yr)
}

/** Lookup release year from the curated catalog (Numbers export). */
export function lookupPhoneReleaseYear(
  manufacturer: string | null | undefined,
  modelName: string | null | undefined,
): number | null {
  const mfr = (manufacturer ?? '').trim()
  const model = (modelName ?? '').trim()
  if (!mfr || !model) return null
  return RELEASE_YEAR_BY_KEY.get(catalogKey(mfr, model)) ?? null
}

/** Parse launch year from model suffixes like "6310 (2024)" or "A3 (2024)". */
export function parentheticalLaunchYear(modelName: string | null | undefined): number | null {
  const model = (modelName ?? '').trim()
  if (!model) return null
  const match = model.match(/\((\d{4})\)/)
  if (!match) return null
  const yr = Number(match[1])
  if (!Number.isFinite(yr) || yr < 2010 || yr > new Date().getFullYear() + 1) return null
  return yr
}

/** Minimum selectable listing year for a phone model (catalog DB field or JSON fallback). */
export function resolvePhoneModelMinReleaseYear(
  manufacturer: string | null | undefined,
  modelName: string | null | undefined,
  catalogReleaseYear?: number | null,
): number {
  if (catalogReleaseYear != null && Number.isFinite(catalogReleaseYear)) {
    return catalogReleaseYear
  }
  const fromParen = parentheticalLaunchYear(modelName)
  if (fromParen != null) return fromParen
  const fromJson = lookupPhoneReleaseYear(manufacturer, modelName)
  if (fromJson != null) return fromJson
  return LISTING_RELEASE_YEAR_FLOOR
}

export function clampListingReleaseYear(
  year: number | null | undefined,
  minYear: number,
  maxYear: number = new Date().getFullYear() + 1,
): number {
  const floor = Math.max(LISTING_RELEASE_YEAR_FLOOR, minYear)
  const cap = Math.max(floor, maxYear)
  if (year == null || !Number.isFinite(year)) return floor
  return Math.min(cap, Math.max(floor, year))
}

export interface PhoneModelReleaseYearInput {
  manufacturer: string
  model_name: string
  release_year?: number | null
}

/** When a model is picked, default listing year to its launch year. */
export function defaultListingReleaseYearForModel(
  model: PhoneModelReleaseYearInput,
  maxYear: number = new Date().getFullYear() + 1,
): number {
  return clampListingReleaseYear(
    resolvePhoneModelMinReleaseYear(model.manufacturer, model.model_name, model.release_year),
    resolvePhoneModelMinReleaseYear(model.manufacturer, model.model_name, model.release_year),
    maxYear,
  )
}
